import React from 'react';
import { Home, Sparkles, Search, Truck, Hammer, Zap, LayoutGrid, ArrowRight, CheckCircle, Phone } from 'lucide-react';
import { services, contactInfo } from '../data/mock';
import { ScrollReveal } from './ui/scroll-reveal';

const iconMap = {
  Home,
  Sparkles,
  Search,
  Truck,
  Hammer,
  Zap,
  LayoutGrid
};

const ServicesSection = () => {
  // Featured services (first 3)
  const featuredServices = services.slice(0, 3);
  // Other services
  const otherServices = services.slice(3);

  return (
    <section id="services" className="py-24 overflow-hidden">
      <div className="container">
        {/* Section Header */}
        <ScrollReveal animation="fadeUp">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span 
              className="inline-block text-xs font-semibold uppercase tracking-widest mb-4 px-4 py-1.5 rounded-full"
              style={{ 
                color: 'var(--accent-blue)',
                background: 'var(--accent-blue-light)'
              }}
            >
              What We Offer
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold mb-5" style={{ color: 'var(--text-primary)' }}>
              Professional Cleaning<br />
              <span className="gradient-text">Solutions</span>
            </h2>
            <p className="text-lg" style={{ color: 'var(--text-muted)' }}>
              From routine maintenance to specialized cleaning, we deliver exceptional 
              results with attention to every detail.
            </p>
          </div>
        </ScrollReveal>

        {/* Featured Services - Large Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {featuredServices.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            const gradients = [
              'linear-gradient(135deg, #E8F4FA 0%, #D4EAF5 100%)',
              'linear-gradient(135deg, #EEF6F0 0%, #DCF0E2 100%)',
              'linear-gradient(135deg, #FFF8E8 0%, #FFEFCC 100%)'
            ];
            const iconColors = ['var(--accent-blue)', 'var(--accent-green)', 'var(--accent-gold)'];

            return (
              <ScrollReveal key={service.id} animation="fadeUp" delay={index * 100}>
                <div 
                  className="group relative p-8 rounded-3xl h-full transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl overflow-hidden"
                  style={{ background: gradients[index] }}
                >
                  {/* Decorative circle */}
                  <div 
                    className="absolute -right-10 -top-10 w-40 h-40 rounded-full opacity-30 transition-transform duration-500 group-hover:scale-150"
                    style={{ background: iconColors[index] }}
                  />

                  {/* Icon */}
                  <div 
                    className="relative w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110"
                    style={{ background: 'white', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}
                  >
                    {IconComponent && (
                      <IconComponent size={28} style={{ color: iconColors[index] }} strokeWidth={1.5} />
                    )}
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-semibold mb-3 relative" style={{ color: 'var(--text-primary)' }}>
                    {service.title}
                  </h3>
                  <p className="text-sm leading-relaxed mb-6 relative" style={{ color: 'var(--text-secondary)' }}>
                    {service.description}
                  </p>

                  {/* CTA Link */}
                  <a 
                    href="#contact"
                    className="inline-flex items-center gap-2 text-sm font-semibold relative transition-all duration-300 group-hover:gap-3"
                    style={{ color: iconColors[index] }}
                  >
                    Book Now
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </a>
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        {/* Other Services - Compact Grid */}
        <ScrollReveal animation="fadeUp" delay={300}>
          <div 
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 p-6 rounded-3xl mb-12"
            style={{ background: 'var(--bg-section)' }}
          >
            {otherServices.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <a
                  key={service.id}
                  href="#contact"
                  className="group flex items-center gap-4 p-4 rounded-2xl transition-all duration-300 hover:bg-white hover:shadow-lg"
                >
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
                    style={{ background: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}
                  >
                    {IconComponent && (
                      <IconComponent size={22} style={{ color: 'var(--navy-primary)' }} strokeWidth={1.5} />
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm mb-0.5" style={{ color: 'var(--text-primary)' }}>
                      {service.title}
                    </h4>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      Learn more →
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        </ScrollReveal>

        {/* Value Propositions */}
        <ScrollReveal animation="fadeUp" delay={400}>
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {[
              { text: 'Licensed & Insured', icon: CheckCircle },
              { text: 'Eco-Friendly Products', icon: CheckCircle },
              { text: 'Satisfaction Guaranteed', icon: CheckCircle },
              { text: 'Flexible Scheduling', icon: CheckCircle }
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 justify-center md:justify-start">
                <item.icon size={18} style={{ color: 'var(--accent-green)' }} />
                <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                  {item.text}
                </span>
              </div>
            ))}
          </div>
        </ScrollReveal>

        {/* CTA Banner */}
        <ScrollReveal animation="fadeUp" delay={500}>
          <div 
            className="relative overflow-hidden rounded-3xl p-8 md:p-12"
            style={{ 
              background: 'linear-gradient(135deg, var(--navy-primary) 0%, var(--navy-light) 100%)'
            }}
          >
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10" 
              style={{ background: 'radial-gradient(circle, white 0%, transparent 70%)', transform: 'translate(30%, -50%)' }} 
            />
            
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center md:text-left">
                <h3 className="text-2xl md:text-3xl font-semibold text-white mb-3">
                  Ready for a Spotless Home?
                </h3>
                <p className="text-white/70 max-w-md">
                  Book your cleaning today and experience the difference. 
                  We guarantee your satisfaction.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href={`tel:${contactInfo.phone.replace(/[^0-9]/g, '')}`}
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold transition-all duration-300 hover:scale-105"
                  style={{ background: 'white', color: 'var(--navy-primary)' }}
                >
                  <Phone size={18} />
                  Call Now
                </a>
                <a 
                  href="#contact"
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold transition-all duration-300 hover:scale-105 hover:opacity-90"
                  style={{ background: 'var(--accent-gold)', color: 'var(--navy-dark)' }}
                >
                  Get Free Quote
                  <ArrowRight size={18} />
                </a>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default ServicesSection;
